import { Logo } from '@atoms/logo/logo.comp';
import {
  EnvironmentSpan,
  HeaderDivider,
  HeaderLeft,
  HeaderMenu,
  HeaderMenuItem,
  HeaderMenuItemLabel,
  HeaderMenuItemLink,
  HeaderRight,
  MainHeaderRoot
} from '@components/header/header.comp';
import { ProcessTagBadge } from '@components/process-tag-badge';
import { Avatar, AvatarFallback, AvatarImage } from '@components/ui/avatar';
import useProcess from '@hooks/useProcess';
import { Skeleton } from '@mui/joy';
import type { StudioUser } from '@stratumn/studio-shared';
import { observer } from 'mobx-react';
import { useNavigate, useParams } from 'react-router-dom';
import { ParamsList } from '@/routes/routes.types';
import { ENV, EnvironmentName } from '@/utils/constants';

import { ProcessSelector } from '../processSelector';
import { WorkflowSelector } from '../workflowSelector';
import HeaderNavRight from './headerNavRight';
import { HeaderUserMenu } from './headerUserMenu';

type HeaderProps = {
  user: StudioUser;
};

export const MainHeader = observer(({ user }: HeaderProps) => {
  const process = useProcess();

  const navigate = useNavigate();
  const currentWorkflowId = useParams()[ParamsList.WorkflowId];

  const isEnvironmentDisplayed: boolean =
    ENV !== EnvironmentName.Release &&
    !!ENV &&
    Object.values(EnvironmentName).includes(ENV);

  const hasBeenGenerated =
    process?.workflows?.some(wf => wf.published_id !== null) ||
    process?.published;

  return (
    <MainHeaderRoot isHomepage={!process}>
      <HeaderLeft>
        <Logo />
        <HeaderDivider />
        <HeaderMenu>
          {process?.name && (
            <HeaderMenuItem>
              <HeaderMenuItemLink
                onClick={() => navigate(`${process.id}/general`)}
              >
                <Avatar className="mr-1.5 h-5 w-5 text-[9px]">
                  <AvatarImage className="object-cover" src={process?.icon} />
                  <AvatarFallback className="bg-primary/10 font-medium text-[9px] text-primary">
                    {process.name
                      .trim()
                      .split(/\s+/)
                      .slice(0, 2)
                      .map(w => w[0])
                      .join('')
                      .toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <HeaderMenuItemLabel>{process.name}</HeaderMenuItemLabel>
                <ProcessTagBadge tag={process.tag} />
              </HeaderMenuItemLink>
            </HeaderMenuItem>
          )}
          <HeaderMenuItem>
            <ProcessSelector />
          </HeaderMenuItem>
          {currentWorkflowId && (
            <>
              <HeaderDivider />
              {process ? (
                <HeaderMenuItem>
                  <HeaderMenuItemLink
                    onClick={() =>
                      navigate(`${process.id}/${currentWorkflowId}/editor`)
                    }
                  >
                    <HeaderMenuItemLabel>
                      {
                        process.workflows.find(
                          wf => wf.id === currentWorkflowId
                        )?.name
                      }
                    </HeaderMenuItemLabel>
                  </HeaderMenuItemLink>
                </HeaderMenuItem>
              ) : (
                <Skeleton className="h-[20px] w-[100px]" />
              )}
              <HeaderMenuItem>
                <WorkflowSelector />
              </HeaderMenuItem>
            </>
          )}
        </HeaderMenu>
        {isEnvironmentDisplayed && <EnvironmentSpan>{ENV}</EnvironmentSpan>}
      </HeaderLeft>
      <HeaderRight>
        {process !== undefined && (
          <HeaderNavRight
            can_edit={process?.permission.can_edit}
            currentProcessId={process.id}
            hasBeenGenerated={hasBeenGenerated}
            user={user}
            workflowList={process.workflows}
          />
        )}
        <HeaderUserMenu user={user} />
      </HeaderRight>
    </MainHeaderRoot>
  );
});
